import { isNil } from "lodash";

import { NO_VALUE_DISPLAY } from "../../constants";
import { RideStationCategory } from "../../enums";
import type {
  IDropdownItem,
  IDropdownItemStations,
  IPassengerForStationDto,
  ISelectListItem,
} from "../../models";
import { Log } from "../log.helpers";
import {
  getSinglePassengerDestinationStationIds,
  getSinglePassengerSourceStationsIds,
} from "./rides.add-passenger.helpers";

export const filterHomeStationsCategory = ({
  passengers,
  stationCategories,
  stationsDropdownList: stationsOutDropdownList,
}: {
  passengers: IPassengerForStationDto[] | undefined;
  stationCategories: RideStationCategory[] | undefined;
  stationsDropdownList: IDropdownItemStations[];
}): IDropdownItemStations[] => {
  if (!stationCategories?.length) {
    return stationsOutDropdownList;
  }

  const combinedFilteredStations: IDropdownItemStations[] = [];
  const seenStationIds = new Set<number>();
  // Depends only on `passengers`, so it is built once rather than rebuilt for every station in the loop.
  const sourceStationIdsSet = new Set(
    passengers?.flatMap(p => getSinglePassengerSourceStationsIds(p)) ?? [],
  );

  stationsOutDropdownList.forEach(station => {
    let shouldInclude = false;
    stationCategories.forEach(category => {
      switch (category) {
        case RideStationCategory.HomeStation:
          if (!station.isOfficial) {
            shouldInclude = true;
          }

          break;
        case RideStationCategory.OfficialStation:
          if (station.isOfficial) {
            shouldInclude = true;
          }

          break;
        case RideStationCategory.WorkStation:
          if (station.isDestination) {
            shouldInclude = true;
          }

          break;

        case RideStationCategory.PassengerStation: {
          if (sourceStationIdsSet.has(Number(station.value))) {
            shouldInclude = true;
          }

          break;
        }

        default:
          break;
      }

      if (shouldInclude) {
        return; // Break out of inner forEach loop (optimized version without loop is much less readable)
      }
    });

    if (shouldInclude && !seenStationIds.has(Number(station.value))) {
      combinedFilteredStations.push(station);
      seenStationIds.add(Number(station.value));
    }
  });
  Log.debug(
    `Combined filtered HOME stations by categories: [${stationCategories?.join(",") || NO_VALUE_DISPLAY}]`,
    combinedFilteredStations,
  );
  return combinedFilteredStations;
};

/* separate functions for Home and work categories to easily implement different logic if needed */
export const filterWorkStationsCategory = ({
  passengers,
  stationCategories,
  stationsDropdownList: stationsOutDropdownList,
}: {
  passengers: IPassengerForStationDto[] | undefined;
  stationCategories: RideStationCategory[] | undefined;
  stationsDropdownList: IDropdownItemStations[];
}): IDropdownItemStations[] => {
  if (!stationCategories?.length) {
    return stationsOutDropdownList;
  }

  const combinedFilteredStations: IDropdownItemStations[] = [];
  const seenStationIds = new Set<number>();
  // Depends only on `passengers`, so it is built once rather than rebuilt for every station in the loop.
  const destinationStationIdsSet = new Set(
    passengers?.flatMap(p => getSinglePassengerDestinationStationIds(p)) ?? [],
  );

  stationsOutDropdownList.forEach(station => {
    let shouldInclude = false;
    stationCategories.forEach(category => {
      switch (category) {
        case RideStationCategory.HomeStation:
          if (!station.isOfficial) {
            shouldInclude = true;
          }

          break;
        case RideStationCategory.OfficialStation:
          if (station.isOfficial) {
            shouldInclude = true;
          }

          break;
        case RideStationCategory.WorkStation:
          if (station.isDestination) {
            shouldInclude = true;
          }

          break;

        case RideStationCategory.PassengerStation: {
          if (destinationStationIdsSet.has(Number(station.value))) {
            shouldInclude = true;
          }

          break;
        }

        default:
          break;
      }

      if (shouldInclude) {
        return; // Break out of inner forEach loop (optimized version without loop is much less readable)
      }
    });

    if (shouldInclude && !seenStationIds.has(Number(station.value))) {
      combinedFilteredStations.push(station);
      seenStationIds.add(Number(station.value));
    }
  });
  Log.debug(
    `Combined filtered  WORK stations by categories: [${stationCategories?.join(",") || NO_VALUE_DISPLAY}]`,
    combinedFilteredStations,
  );
  return combinedFilteredStations;
};

/**
 * Keeps the field's own station listed when the category filter drops it - otherwise a station chosen
 * outside the configured categories (e.g. one just quick-added) renders as an empty field.
 */
export const includeSelectedStation = <T extends IDropdownItem>({
  filteredStations,
  selectedStationId,
  allStations,
}: {
  filteredStations: T[];
  selectedStationId: IDropdownItem["value"] | null | undefined;
  allStations: T[];
}): T[] => {
  if (
    isNil(selectedStationId) ||
    filteredStations.some(
      ({ value }) => String(value) === String(selectedStationId),
    )
  ) {
    return filteredStations;
  }

  const selectedStation = allStations.find(
    ({ value }) => String(value) === String(selectedStationId),
  );

  return selectedStation
    ? [...filteredStations, selectedStation]
    : filteredStations;
};

/**
 * Ids a home-side booking field offers: the home stations the categories allow, plus the stations created in
 * the form - those are offered in both home fields even when the categories would filter them out.
 */
export const getOfferedHomeStationIds = ({
  homeStations,
  addedStationIds,
}: {
  homeStations: IDropdownItemStations[];
  addedStationIds: number[];
}): Set<string> =>
  new Set([
    ...homeStations.map(({ value }) => String(value)),
    ...addedStationIds.map(String),
  ]);

/** Per-option form of `includeSelectedStation`, for dropdowns that filter their own fetched items. */
export const isStationOptionShown = ({
  stationId,
  listedStationIds,
  selectedStationId,
}: {
  stationId: ISelectListItem["value"];
  listedStationIds: Set<string>;
  selectedStationId: IDropdownItem["value"] | null | undefined;
}): boolean =>
  listedStationIds.has(String(stationId)) ||
  (!isNil(selectedStationId) &&
    String(stationId) === String(selectedStationId));
